import AvatarCompany, {
  AvatarCompanyData,
} from "@/components/company/avatar-company";
import { useCompanyLinkInPanel } from "@/hooks/useCompanyLinkInPanel";
import { useTranslation } from "@/hooks/useTranslation";
import { internalLinkTarget } from "@/lib/internal-link-target";
import { cn } from "@/lib/utils";
import { CompanyDataToolOutput } from "@shaarp/common";
import { ToolUIPart } from "ai";
import {
  ArrowUpRightIcon,
  Building2Icon,
  CheckCircleIcon,
  Loader2Icon,
  SearchXIcon,
  XCircleIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Shimmer } from "../ai-elements/shimmer";

const CARD_CLASSNAME =
  "overflow-hidden w-full rounded-xl border bg-background not-prose";
const HEADER_CLASSNAME =
  "flex w-full items-center gap-2.5 p-3 text-left text-sm text-muted-foreground";
const ROW_CLASSNAME =
  "flex w-full items-center gap-2.5 text-left text-sm text-muted-foreground";

/**
 * The tool always returns a structured `CompanyDataToolOutput`: coverage can
 * be 'complete', 'partial' or 'absent', but the shape itself never varies and
 * there is no plain-string fallback to guard against. The check only exists
 * to narrow `unknown` safely before reading the company's name and page.
 */
function isStructuredOutput(output: unknown): output is CompanyDataToolOutput {
  return (
    !!output &&
    typeof output === "object" &&
    typeof (output as CompanyDataToolOutput).coverage === "string" &&
    Array.isArray((output as CompanyDataToolOutput).sections)
  );
}

/**
 * The company the assistant just read from Shaarp's database, as one line: its
 * name, a link to its Shaarp page, and whether the read succeeded.
 *
 * Deliberately not a data viewer. What the tool returned is already in the
 * answer the assistant writes underneath; repeating the sections, their
 * freshness and their origin here only stole room from it. Whoever wants the
 * figures themselves opens the company page.
 */
export function ChatToolCompanyData({
  state,
  output,
}: {
  state: ToolUIPart["state"];
  output: unknown;
}) {
  const { t } = useTranslation();
  const openInPanel = useCompanyLinkInPanel();

  // A failed call keeps its state persisted, so falling through to the
  // spinner would leave the card loading forever, including after a reload.
  if (state === "output-error" || state === "output-denied") {
    return (
      <div className={CARD_CLASSNAME}>
        <div className={HEADER_CLASSNAME}>
          <Building2Icon className="size-4 shrink-0" />
          <span className="font-medium text-foreground">
            {t("chat.message.tool.companyData.error")}
          </span>
          <XCircleIcon className="size-3.5 shrink-0 text-red-600" />
        </div>
      </div>
    );
  }

  if (state !== "output-available") {
    return (
      <div className={CARD_CLASSNAME}>
        <div className={HEADER_CLASSNAME}>
          <Building2Icon className="size-4 shrink-0" />
          <Shimmer className="font-medium">
            {t("chat.message.tool.companyData.loading")}
          </Shimmer>
          <Loader2Icon className="size-3.5 shrink-0 animate-spin" />
        </div>
      </div>
    );
  }

  if (!isStructuredOutput(output)) {
    return null;
  }

  // Found nothing, and that is a normal outcome rather than a failure: the
  // company simply is not in Shaarp's database, and the model hands the
  // question to webSearch. It gets its own muted state — the green check of
  // the success layout next to "unknown company" said two opposite things.
  if (!output.companyId) {
    return (
      <div className={CARD_CLASSNAME}>
        <div className={HEADER_CLASSNAME}>
          <Building2Icon className="size-4 shrink-0" />
          <span className="flex-1 min-w-0 font-medium truncate">
            {t("chat.message.tool.companyData.unknownCompany")}
          </span>
          <SearchXIcon className="size-3.5 shrink-0" />
        </div>
      </div>
    );
  }

  /*
    The tool returns no logo, only what identifies the company. That is enough
    for the avatar: its first fallback is built from the domain, and a company
    without one lands on the gradient initial like everywhere else in the app.
  */
  const company: AvatarCompanyData = {
    id: output.companyId,
    name: output.name ?? "",
    domain: output.domain,
    logo: null,
    thumbnails: [],
  };

  return (
    <div className={CARD_CLASSNAME}>
      {/*
        The title sits on its own line rather than before the name: together
        they overflow a narrow screen, and truncating either of them is worse
        than one extra line.
      */}
      <div className="flex flex-col gap-1.5 p-3">
        <span className="text-xs text-muted-foreground">
          {t("chat.message.tool.companyData.title")}
        </span>
        <div className={ROW_CLASSNAME}>
          <AvatarCompany company={company} className="size-5 shrink-0" />
          {/*
            Internal route, opened in a new tab on a desktop: the conversation
            is the user's place of work, and losing it to read a company page
            is not a trade they asked for. In place on a phone
            (internalLinkTarget).
          */}
          <Link
            to={`/company/${output.companyId}`}
            target={internalLinkTarget()}
            rel="noreferrer"
            onClick={(event) =>
              output.companyId &&
              openInPanel(event, {
                id: output.companyId,
                label: output.name ?? "",
              })
            }
            className={cn(
              "flex min-w-0 flex-1 items-center gap-1 font-medium text-foreground",
              "transition-colors hover:text-primary hover:underline underline-offset-2"
            )}
          >
            <span className="truncate">
              {output.name ?? t("chat.message.tool.companyData.unknownCompany")}
            </span>
            <ArrowUpRightIcon className="size-3.5 shrink-0" />
          </Link>
          <CheckCircleIcon className="size-3.5 shrink-0 text-green-600" />
        </div>
      </div>
    </div>
  );
}
